import { useCallback, useMemo, useRef, useState } from "react";
import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper, { PaperProps } from "@mui/material/Paper";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentClinic,
  selectCurrentRegionClinicsIds,
  selectHasRegions
} from "features/auth/auth-slice";
import useReportFilterLock from "features/reports/hooks/use-report-filter-lock.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetStaffQuery } from "services/staff";
import { StaffFilters } from "services/types/staff.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SELECT_ALL_VALUE = "__select_all__";
const SELECT_ALL_OPTION = {
  label: "Select All",
  value: SELECT_ALL_VALUE,
  is_active: true
};

type StaffOption = { label: string; value: string; is_active: boolean };

const baseFilterOptions = createFilterOptions<StaffOption>({
  matchFrom: "any",
  stringify: (option) => option.label
});

type SelectProviderProps = {
  label?: string;
  includeOtherClinics?: boolean;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  isActive?: boolean | null;
  required?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  enablePortal?: boolean;
  sx?: SxProps;
  ignoreRegionsFilter?: boolean;
  ignoreClinicsFilter?: boolean;
  groupName?: StaffFilters["groupName"];
  enableSelectAll?: boolean;
  excludeSelectAllValues?: string | string[];
};

export type RhfSelectProvider<T extends FieldValues> = SelectProviderProps &
  UseControllerProps<T>;

export default function SelectProvider<T extends FieldValues>({
  control,
  label = "By Provider",
  name,
  includeOtherClinics,
  ignoreRegionsFilter,
  ignoreClinicsFilter,
  onOptionSelected,
  isActive = true,
  required = false,
  multiple = false,
  openOnFocus = false,
  enablePortal = false,
  autoFocus,
  groupName,
  sx,
  enableSelectAll = false,
  excludeSelectAllValues,
  rules
}: RhfSelectProvider<T>) {
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const { isAdminOrBusinessOwner } = useUserRoles();

  // ENG-1595: on a report scoped to the user's own records, this filter is pinned to them.
  const filterLock = useReportFilterLock();
  const isStaffLocked = filterLock.lockedStaffFilter === name;

  const [showInactive, setShowInactive] = useState(false);

  const canToggleInactive = isAdminOrBusinessOwner && isActive === true;
  const effectiveIsActive = canToggleInactive && showInactive ? null : isActive;

  const clinicIds = useMemo(() => {
    if (ignoreClinicsFilter) {
      return undefined;
    }
    const clinicFilters =
      !includeOtherClinics && currentClinic?.id
        ? [currentClinic.id]
        : undefined;
    if (hasRegions && includeOtherClinics && !ignoreRegionsFilter) {
      return currentRegionClinicsIds;
    }
    return clinicFilters;
  }, [
    ignoreRegionsFilter,
    ignoreClinicsFilter,
    currentRegionClinicsIds,
    includeOtherClinics,
    currentClinic?.id,
    hasRegions
  ]);

  const staffFilters: StaffFilters = {
    is_active: effectiveIsActive,
    clinicIds,
    groupName
  };

  const { data: staff = [], isLoading } = useGetStaffQuery(staffFilters);

  const staffCountValues = useMemo(() => {
    return staff.reduce(
      (acc, staffMember) => {
        const label = `${staffMember.fullName}${
          staffMember.is_active ? "" : " (Inactive)"
        }`;
        if (!acc[label]) {
          acc[label] = 0;
        }
        acc[label] += 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [staff]);

  const excludedValues = useMemo(() => {
    if (!excludeSelectAllValues) return [];
    return Array.isArray(excludeSelectAllValues)
      ? excludeSelectAllValues
      : [excludeSelectAllValues];
  }, [excludeSelectAllValues]);

  const staffOptions = useMemo(
    () =>
      staff.map((staffMember) => {
        const baseLabel = `${staffMember.fullName}${
          staffMember.is_active ? "" : " (Inactive)"
        }`;
        const count = staffCountValues[baseLabel];
        const label =
          count > 1 ? `${baseLabel} (${staffMember.email})` : baseLabel;
        return {
          label,
          value: String(staffMember.id),
          is_active: staffMember.is_active
        };
      }),
    [staff, staffCountValues]
  );

  const filteredStaffOptions = useMemo(() => {
    if (isStaffLocked) {
      return staffOptions.filter((o) => o.value === filterLock.lockedStaffId);
    }
    return excludedValues.length > 0
      ? staffOptions.filter((o) => !excludedValues.includes(o.value))
      : staffOptions;
  }, [staffOptions, excludedValues, isStaffLocked, filterLock.lockedStaffId]);

  const options = useMemo(
    () =>
      enableSelectAll && multiple
        ? [SELECT_ALL_OPTION, ...filteredStaffOptions]
        : filteredStaffOptions,
    [enableSelectAll, multiple, filteredStaffOptions]
  );

  const filterOptions = useMemo(
    () =>
      enableSelectAll && multiple
        ? (
            opts: StaffOption[],
            state: Parameters<typeof baseFilterOptions>[1]
          ) => {
            const filtered = baseFilterOptions(
              opts.filter((o) => o.value !== SELECT_ALL_VALUE),
              state
            );
            return [SELECT_ALL_OPTION, ...filtered];
          }
        : baseFilterOptions,
    [enableSelectAll, multiple]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const transformValue = useCallback(
    (selected: StaffOption | StaffOption[] | null) => {
      if (
        multiple &&
        enableSelectAll &&
        Array.isArray(selected) &&
        selected.some((o) => o.value === SELECT_ALL_VALUE)
      ) {
        const currentValues = selected.filter(
          (o) => o.value !== SELECT_ALL_VALUE
        );
        const allAlreadySelected = filteredStaffOptions.every((o) =>
          currentValues.some((v) => v.value === o.value)
        );
        inputRef.current?.blur();
        return (
          allAlreadySelected ? [] : filteredStaffOptions
        ) as StaffOption[];
      }
      return Array.isArray(selected)
        ? selected.filter((o) => o.value !== SELECT_ALL_VALUE)
        : selected;
    },
    [multiple, enableSelectAll, filteredStaffOptions]
  );

  const handleOptionSelected = useCallback(
    (selected: StaffOption | StaffOption[] | null) => {
      if (onOptionSelected) {
        const cleaned = Array.isArray(selected)
          ? selected.filter((o) => o.value !== SELECT_ALL_VALUE)
          : selected;
        onOptionSelected(cleaned);
      }
    },
    [onOptionSelected]
  );

  const showInactiveRef = useRef(showInactive);
  showInactiveRef.current = showInactive;

  const InactiveTogglePaper = useMemo(() => {
    const Component = ({ children, ...rest }: PaperProps) => (
      <Paper {...rest}>
        <FormControlLabel
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            display: "flex",
            m: 0,
            px: 1.5,
            py: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
          control={
            <Checkbox
              size="small"
              checked={showInactiveRef.current}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
          }
          label="Show inactive users"
        />
        {children}
      </Paper>
    );
    Component.displayName = "SelectProviderListboxPaper";
    return Component;
  }, []);

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      multiple={multiple}
      label={label}
      isLoading={isLoading}
      required={required}
      filterOptions={filterOptions}
      options={options}
      getOptionLabel={(option) => option?.label ?? ""}
      renderOption={(
        props: object,
        option: StaffOption,
        { selected }: { selected: boolean }
      ) => {
        const isSelectAll = option.value === SELECT_ALL_VALUE;
        return (
          <ListItem {...props} key={option.value}>
            {multiple && !isSelectAll && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            <ListItemText
              primaryTypographyProps={
                isSelectAll ? { fontWeight: "bold" } : undefined
              }
            >
              {option?.label}
            </ListItemText>
          </ListItem>
        );
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      PaperComponent={canToggleInactive ? InactiveTogglePaper : undefined}
      control={control}
      rules={rules}
      transformValue={transformValue as any}
      onOptionSelected={handleOptionSelected}
      inputRef={inputRef}
      disabled={isStaffLocked}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
